import type { FavoriteViewSearch } from '@/contexts/favorites/favoriteView'
import { VpDrawer, VpDrawerBody, VpDrawerCloseButton, VpDrawerDialog, VpDrawerHeader } from '@vtmn-play/react'
import { useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { FavoriteViewFolderSelector } from '@/components/FavoriteViews/FavoriteViewFolderSelector'
import { FavoriteViewFormFooter } from '@/components/FavoriteViews/FavoriteViewFormFooter'
import { useFavoriteViewForm } from '@/components/FavoriteViews/useFavoriteViewForm'
import './favorite-view-panel.scss'

interface FavoriteViewPanelProps {
  isOpen: boolean
  search: FavoriteViewSearch | null
  onClose: () => void
}

export function FavoriteViewPanel({ isOpen, search, onClose }: FavoriteViewPanelProps) {
  const { t } = useTranslation()
  const form = useFavoriteViewForm({ search, onClose })
  const drawerCloseButtonRef = useRef<HTMLButtonElement>(null)

  return isOpen
    ? createPortal(
        <div className="favorite-view-panel__layer" data-testid="favorite-view-layer">
          <div className="favorite-view-panel__backdrop" aria-hidden="true" data-testid="favorite-view-backdrop" />
          <VpDrawer closeOnClickOutside open={isOpen} onOpen={() => {}} onClose={form.closePanel}>
            <VpDrawerDialog
              className="favorite-view-panel__dialog"
              style={{ zIndex: 1 }}
              aria-label={t('favorite-views.panel.title')}
              data-testid="favorite-view-panel"
            >
              <VpDrawerHeader>
                {t('favorite-views.panel.title')}
                <VpDrawerCloseButton
                  ref={drawerCloseButtonRef}
                  aria-label={t('close')}
                  data-testid="favorite-view-panel__close"
                />
              </VpDrawerHeader>
              <form className="favorite-view-panel__form" onSubmit={form.handleSubmit}>
                <VpDrawerBody className="favorite-view-panel__body">
                  <label className="favorite-view-panel__field" htmlFor="favorite-view-title">
                    <span>{t('favorite-views.panel.view-title')}</span>
                    <input
                      id="favorite-view-title"
                      autoComplete="off"
                      maxLength={100}
                      value={form.title}
                      onChange={event => form.setTitle(event.target.value)}
                      data-testid="favorite-view-title"
                    />
                  </label>

                  <FavoriteViewFolderSelector
                    folders={form.folders}
                    totalViews={form.totalViews}
                    activeFolderId={form.activeFolderId}
                    hasLoadError={form.hasFolderLoadError}
                    draft={form.draft}
                    onAddFolder={form.addNewFolder}
                    onCancelDraft={form.cancelNewFolder}
                    onDraftNameChange={form.onDraftNameChange}
                    onEmojiSelect={form.onEmojiSelect}
                    onFolderSelect={form.onFolderSelect}
                  />
                </VpDrawerBody>
                <FavoriteViewFormFooter
                  onCancel={() => drawerCloseButtonRef.current?.click()}
                  isSaveDisabled={form.isSaveDisabled}
                  saveLabel={form.isSaving ? t('favorite-views.panel.saving') : t('save')}
                  saveTestId="favorite-view-panel__save"
                  cancelTestId="favorite-view-panel__cancel"
                />
              </form>
            </VpDrawerDialog>
          </VpDrawer>
        </div>,
        document.body,
      )
    : null
}
